<script>
    /* States */
    import { explorerState } from "@app/store/explorerStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { setExternalDataState } from "@app/store/externalDataStore.svelte.js";
    import { showModal, hideModal } from "@app/store/modalStore.svelte.js";
    import { projectState, updateProjectId } from "@app/store/projectStore.svelte.js";
    import { metadataState } from "@app/store/metadataStore.svelte.js";
    import { speakerState } from "@app/store/speakerStore.svelte.js";
    import { issueState } from "@app/store/issueStore.svelte.js";
    import { styleState } from "@app/store/styleStore.svelte.js";
    import {
    resetHistory, insertHistoryAction
} from '@app/store/historyStore.svelte.js';
    import { environment } from "@app/store/envStore.svelte.js";
    /* Libraries */
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import { saveLastExplorerLocation, saveLastExplorerLocationFromRecord } from "@app/lib/explorerLastLocation.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { saveAs } from "file-saver";
    import * as m from "@app/paraglide/messages.js";
    /* Firebase */
    import firebase from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import storage from "@app/configs/storage.js";
    import "firebase/functions";    
    /* CC LIB */
    import workflowDict from "@cclib/dict/workflow.js";
    import { ccprjFunc } from '@app/lib/ccprj.js';
    /* Export Presets + Batch Export */
    import { getPresetsByType, getPresetById, PRESET_TYPES } from "@app/store/presetStore.svelte.js";
    import { isEventGroupValid, buildExportFileName, encodeEventGroupWithPreset } from "@app/lib/batchProjectExport.js";
    const iconMap = {
        "folder" : "bi-folder-fill",
        "project" : "bi-file-earmark-play",
        "team project" : "bi-people",
        "work order" : "bi-clipboard-check"
    }

    let userId = firebase.auth().currentUser.uid;
    let userEmail = firebase.auth().currentUser.email;
    let teamId = authState.team ? authState.team.id : null;
    let homeDbRef = db.collection("users").doc(userId).collection("storage");
    let teamDbRef = teamId ? db.collection("teams").doc(teamId).collection("storage") : null;
    let userProfileRef = db.collection("users").doc(userId);

    let selectedRecord = $state(null);
    let loading = $state(false);
    let openLoading = $state(false); // New loading state for open button
    let availableGroups = $state([]); // Available groups for permissions
    let loadingGroups = $state(false); // Loading state for groups

    /* Batch Export state */
    const EXPORTABLE_TYPES = ["project", "team project", "work order"];
    let batchExporting = $state(false);
    let batchProgress = $state(0);
    let batchStatusMsg = $state("");

    // Records currently selected that can be exported (exclude folders)
    let selectedProjectRecords = $derived(
        explorerState.selectedRecords
            .map((id) => explorerState.records.find((record) => record.id === id))
            .filter((record) => record && EXPORTABLE_TYPES.includes(record.type))
    );
    // Multi-select is active when more than one exportable record is selected
    let multiSelect = $derived(selectedProjectRecords.length > 1);

    // Check if user is admin or manager
    let isAdminOrManager = $derived(
        authState.user && (authState.user.role === "admin" || authState.user.role === "manager")
    );

    $effect(() => {
        // console.log("EFFECT Updating selectedRecord based on explorerState...");
        selectedRecord = explorerState.selectedRecords.length > 0 ? explorerState.records.find(record => record.id === explorerState.selectedRecords[0]) : null;
        
        // Load groups when a team folder is selected
        if (selectedRecord && 
            selectedRecord.type === "folder" && 
            selectedRecord.rootDir === "team" && 
            isAdminOrManager) {
            loadAvailableGroups();
        }
    });
    
    // Add storage paths
    let storageRef = storage.ref();
    let homeStorageBasePath = `users/${userId}/projects/`;
    let teamStorageBasePath = "teams/" + teamId + "/storage/";

    // Download the full project (.ccprj) JSON for a storage record
    async function downloadProjectFile(record) {
        const projectFilePath = record.rootDir === "team"
            ? `${teamStorageBasePath}${record.id}`
            : `${homeStorageBasePath}${record.id}`;

        let url;
        if (record.rootDir === "team") {
            const result = await firebase.functions().httpsCallable("v8GetDownloadLink")(projectFilePath);
            url = result.data;
        } else {
            url = await storageRef.child(projectFilePath).getDownloadURL();
        }

        const response = await fetch(url);
        return await response.json();
    }

    // Batch export the selected projects using a chosen subtitle preset
    async function batchExportSelected() {
        if (!environment.online) {
            toast.push(m.explorer_info_batch_online_only(), { classes: ["toast-warning"] });
            return;
        }

        const recordsToExport = selectedProjectRecords;
        if (recordsToExport.length === 0) {
            return;
        }

        // Ensure at least one subtitle preset exists
        const presets = getPresetsByType(PRESET_TYPES.SUBTITLE);
        if (!presets || presets.length === 0) {
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                icon: "info",
                title: m.explorer_info_batch_no_presets_title(),
                text: m.explorer_info_batch_no_presets_text(),
            });
            return;
        }

        // Let the user choose which preset to export with
        const presetOptions = presets.reduce((acc, preset) => {
            acc[preset.id] = `${preset.name} - ${preset.config.trgProfile} (${preset.config.trgExt})`;
            return acc;
        }, {});

        const { value: chosenPresetId } = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_batch_pick_preset_title(),
            input: "select",
            inputOptions: presetOptions,
            inputPlaceholder: m.explorer_info_batch_pick_preset_placeholder(),
            showCancelButton: true,
            confirmButtonText: m.explorer_info_batch_export_button(),
            inputValidator: (value) => {
                if (!value) {
                    return m.explorer_info_batch_pick_preset_required();
                }
            },
        });

        if (!chosenPresetId) {
            return;
        }

        const preset = getPresetById(chosenPresetId);
        if (!preset || !preset.config) {
            toast.push(m.explorer_info_batch_no_presets_text(), { classes: ["toast-danger"] });
            return;
        }

        batchExporting = true;
        batchProgress = 0;
        batchStatusMsg = "";

        let projectsExported = 0;
        let groupsExported = 0;
        const failures = [];
        const trialStatus = authState.status;

        for (let i = 0; i < recordsToExport.length; i++) {
            const record = recordsToExport[i];
            batchStatusMsg = m.explorer_info_batch_progress_status({
                current: i + 1,
                total: recordsToExport.length,
                name: record.name,
            });
            batchProgress = Math.round((i / recordsToExport.length) * 100);

            try {
                const projectJson = await downloadProjectFile(record);
                const eventGroups = Array.isArray(projectJson.eventGroups) ? projectJson.eventGroups : [];
                const validGroups = eventGroups.filter((group) => isEventGroupValid(group));

                if (validGroups.length === 0) {
                    failures.push(m.explorer_info_batch_error_no_groups({ project: record.name }));
                    continue;
                }

                const ctx = {
                    frameRate: projectJson.frameRate,
                    style: projectJson.style || {},
                    trialStatus,
                };

                let projectHadSuccess = false;
                for (const eventGroup of validGroups) {
                    try {
                        const { fileBlob, ext } = await encodeEventGroupWithPreset(eventGroup, preset, ctx);
                        const fileName = buildExportFileName(record.name, eventGroup.name, preset.name, ext);
                        saveAs(fileBlob, fileName, { autoBom: true });
                        groupsExported++;
                        projectHadSuccess = true;
                    } catch (groupError) {
                        console.error("Batch export group failed:", groupError);
                        failures.push(
                            m.explorer_info_batch_error_group({
                                project: record.name,
                                group: eventGroup.name,
                                error: groupError.message || "",
                            })
                        );
                    }
                }

                if (projectHadSuccess) {
                    projectsExported++;
                }
            } catch (projectError) {
                console.error("Batch export project failed:", projectError);
                failures.push(
                    m.explorer_info_batch_error_project({
                        project: record.name,
                        error: projectError.message || "",
                    })
                );
            }
        }

        batchProgress = 100;
        batchExporting = false;
        batchStatusMsg = "";

        // Final summary
        const summaryText = m.explorer_info_batch_complete_summary({
            projects: projectsExported,
            groups: groupsExported,
            failures: failures.length,
        });

        if (failures.length > 0) {
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                icon: groupsExported > 0 ? "warning" : "error",
                title: m.explorer_info_batch_complete_title(),
                html: `<p>${summaryText}</p><div class="text-start mt-3" style="max-height:200px;overflow-y:auto;"><ul class="small mb-0">${failures
                    .map((failure) => `<li>${failure}</li>`)
                    .join("")}</ul></div>`,
            });
            toast.push(summaryText, { classes: ["toast-warning"] });
        } else {
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                icon: "success",
                title: m.explorer_info_batch_complete_title(),
                text: summaryText,
            });
            toast.push(summaryText, { classes: ["toast-success"] });
        }
    }

    // Function to copy record ID to clipboard
    function copyIdToClipboard() {
        if (selectedRecord && selectedRecord.id) {
            navigator.clipboard.writeText(selectedRecord.id)
                .then(() => {
                    console.log('ID copied to clipboard');
                    toast.push(m.explorer_info_copy_id_success_toast(), {
                        classes: ["toast-success"],
                    });
                })
                .catch(err => {
                    console.error('Failed to copy ID: ', err);
                    toast.push(m.explorer_info_copy_id_failed_toast({ error: err.message }), {
                        classes: ["toast-danger"],
                    });
                });
        }    }

    function normalizeRootDir(record = {}) {
        if (record.rootDir === "personal" || record.rootDir === "team") {
            return record.rootDir;
        }

        if (record.rootDir === "user" || record.scope === "user" || record.scope === "personal") {
            return "personal";
        }

        if (record.rootDir === "team" || record.scope === "team") {
            return "team";
        }

        return "personal";
    }

    function getRecordId(record = {}) {
        if (record.id) {
            return record.id;
        }

        if (record.recordId) {
            return record.recordId;
        }

        if (typeof record.objectID === "string" && record.objectID.includes(":")) {
            return record.objectID.split(":").pop();
        }

        return record.objectID;
    }

    async function hydrateSearchFolder(record) {
        const rootDir = normalizeRootDir(record);
        const dbRef = rootDir === "personal" ? homeDbRef : teamDbRef;
        const folderId = getRecordId(record);

        if (!dbRef) {
            throw new Error(`Invalid database reference for rootDir: ${rootDir}`);
        }

        if (!folderId) {
            throw new Error("Missing folder record ID");
        }

        const folderDoc = await dbRef.doc(folderId).get();

        if (!folderDoc.exists) {
            return {
                ...record,
                id: folderId,
                rootDir,
            };
        }

        return {
            ...folderDoc.data(),
            id: folderDoc.data().id || folderDoc.id,
            rootDir,
        };
    }

    async function openSearchFolder(record) {
        const folder = await hydrateSearchFolder(record);

        explorerState.rootDir = folder.rootDir;
        explorerState.view = "table";
        await buildFolderNavigationPath(folder);
        await getRecords(folder.id);
    }
    
    // Get records where folder Id is the records locationId
    async function getRecords(folderId = null) {
        try {
            explorerState.records = [];
            explorerState.selectedRecords = [];

            // Don't fetch if we're in a system folder
            if (folderId === "recent" || folderId === "starred" || folderId === "tasks" || explorerState.rootDir === "tasks") {
                return;
            }

            const dbRef = explorerState.rootDir === "personal" ? homeDbRef : teamDbRef;
            let querySnapshot = await dbRef
                .where("locationId", "==", folderId)
                .orderBy("type")
                .orderBy("name", "asc")
                .get();

            querySnapshot.forEach((doc) => {
                explorerState.records = [
                    ...explorerState.records,
                    { ...doc.data(), rootDir: explorerState.rootDir },
                ];
            });
        } catch (err) {
            console.error(err);
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        }
    }

    // Build navigation path for a folder by finding all parent folders
    async function buildFolderNavigationPath(folder) {
        try {
            // Clear current navigation history
            explorerState.navHistory = [];
            
            // If no folder, we're at root
            if (!folder) {
                return;
            }
            
            // Select the appropriate database reference
            const dbRef = folder.rootDir === "personal" ? homeDbRef : teamDbRef;
            if (!dbRef) {
                throw new Error(`Invalid database reference for rootDir: ${folder.rootDir}`);
            }
            
            // First add the current folder to the path
            let folderPath = [];
            
            // If folder has no locationId, it's at root level already
            if (!folder.locationId) {
                explorerState.navHistory = [folder];
                return;
            }
            
            // Start with the current folder's locationId to find its parent
            let currentFolderId = folder.locationId;
            let pathComplete = false;
            let safetyCounter = 0; // Prevent infinite loops
            const MAX_DEPTH = 20; // Maximum folder nesting depth
            
            // Keep traversing up until we reach the root or hit safety limit
            while (currentFolderId && !pathComplete && safetyCounter < MAX_DEPTH) {
                safetyCounter++;
                
                try {
                    // Get the parent folder
                    const parentDoc = await dbRef.doc(currentFolderId).get();
                    
                    if (parentDoc.exists) {
                        const parentData = parentDoc.data();
                        const parentFolder = {
                            ...parentData,
                            id: parentData.id || parentDoc.id,
                            rootDir: folder.rootDir
                        };
                        
                        // Add to path (at the beginning)
                        folderPath.unshift(parentFolder);
                        
                        // Continue with parent's parent if it exists
                        if (parentFolder.locationId) {
                            currentFolderId = parentFolder.locationId;
                        } else {
                            pathComplete = true;
                        }
                    } else {
                        pathComplete = true;
                    }
                } catch (err) {
                    console.error(`Error fetching parent folder: ${err.message}`);
                    pathComplete = true;
                }
            }
            
            if (safetyCounter >= MAX_DEPTH) {
                console.warn("Possible circular reference in folder structure, path building stopped");
            }
            
            // Add the original folder at the end
            folderPath.push(folder);
            
            // Update navigation history with the complete path
            explorerState.navHistory = folderPath;
            
        } catch (error) {
            console.error("Error building folder navigation path:", error);
            throw error;
        }
    }
      // Action functions
    async function openRecord() {
        if (!selectedRecord) return;
        
        openLoading = true;
        
        try {
            // Handle folder navigation
            if (selectedRecord.type === "folder") {
                if (explorerState.rootDir === "search") {
                    await openSearchFolder(selectedRecord);
                    return;
                }

                // Check if we're in a system result view
                if (explorerState.rootDir === "recent" || explorerState.rootDir === "starred" || explorerState.rootDir === "tasks") {
                    // Switch to the appropriate rootDir based on the folder's rootDir
                    explorerState.rootDir = selectedRecord.rootDir;
                    
                    // Build the navigation path by finding all parent folders
                    await buildFolderNavigationPath(selectedRecord);
                } else {
                    // Regular folder navigation in normal view
                    explorerState.navHistory = [...explorerState.navHistory, selectedRecord];
                }
                
                // Navigate to the folder contents
                await getRecords(selectedRecord.id);
            } else if (selectedRecord.type === "project" || selectedRecord.type === "team project" || selectedRecord.type === "work order") {
                try {
                    // Determine the storage path based on whether it's personal or team
                    const projectFilePath = selectedRecord.rootDir === "team" 
                        ? `${teamStorageBasePath}${selectedRecord.id}` 
                        : `${homeStorageBasePath}${selectedRecord.id}`;
                    
                    // console.log(`Fetching file from: ${projectFilePath}`);
                    
                    // Get the URL for the file
                    let url;
                    if (selectedRecord.rootDir === "team") {
                        const result = await firebase.functions().httpsCallable("v8GetDownloadLink")(projectFilePath);
                        url = result.data;
                    } else {
                        url = await storageRef.child(projectFilePath).getDownloadURL();
                    }
                    
                    // Fetch the file content
                    const response = await fetch(url);
                    const fileData = await response.json();
                    fileData.rootDir = selectedRecord.rootDir;
                    
                    // Process based on record type
                    if (selectedRecord.type === "work order") {
                        openWorkOrder(fileData);
                    } else if (selectedRecord.type === "project") {
                        openProjectFile(fileData);
                    } else if (selectedRecord.type === "team project") {
                        openTeamProjectFile(fileData);
                    }
                    
                } catch (error) {
                    console.error(`Error opening ${selectedRecord.type}:`, error);
                    toast.push(m.explorer_info_open_failed_toast({ type: selectedRecord.type, error: error.message }), {
                        classes: ["toast-danger"],
                    });
                }
            }
        } finally {
            openLoading = false;
        }
    }

    function openProjectFile(description){
        // Add folderId to description object
        description.folderId = selectedRecord.locationId;

        saveLastExplorerLocationFromRecord(userId, selectedRecord);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call loadProjectFile modal
        showModal("loadProjectFile");
    }

    async function openTeamProjectFile(description) {
        try {
            // Extract team ID and project ID
            const projectTeamId = description.teamId;
            
            if (!projectTeamId || !teamId) {
                throw new Error("Missing team or project ID in file");
            }
            
            // console.log(`Looking up share codes for team project: ${projectTeamId} in team: ${teamId}`);
            
            // Check for project snapshots first (to ensure there's data to share)
            const snapshotsRef = db.collection("teams").doc(teamId)
                .collection("projects").doc(projectTeamId)
                .collection("snapshots");
                
            const snapshotsQuery = await snapshotsRef.limit(1).get();
            
            if (snapshotsQuery.empty) {
                // console.log("No snapshots found for team project, falling back to project file import");
                fallbackToProjectFileImport(description);
                return;
            }
            
            // Look for share codes that reference this project
            const sharesRef = db.collection("teams").doc(teamId).collection("shares");
                
            const sharesQuery = await sharesRef
                .where("projectTeamId", "==", projectTeamId)
                .limit(1)
                .get();            
            
            if (sharesQuery.empty) {
                // console.log("No share codes found for team project, falling back to project file import");
                fallbackToProjectFileImport(description);
                return;
            }
            
            // Get the first share code
            const shareDoc = sharesQuery.docs[0];
            const shareId = shareDoc.id;
            
            // console.log(`Found share code: ${shareId}`);
            
            // Update URL with share code and open teamProjectImport modal
            const url = new URL(window.location.href);
            url.searchParams.set("id", shareId);
            window.history.pushState({}, '', url);
            
            toast.push(m.explorer_info_open_team_project_toast(), {
                classes: ["toast-info"],
            });

            saveLastExplorerLocationFromRecord(userId, selectedRecord);
            
            showModal("teamProjectImport");
            
        } catch (error) {
            console.error("Error processing team project:", error);
            fallbackToProjectFileImport(description);
        }
    }
    
    function fallbackToProjectFileImport(description) {
        console.log("Falling back to project file import method");
        toast.push(m.explorer_info_fallback_project_import_toast(), {
            classes: ["toast-info"],
        });
        
        // Add folderId to description object
        description.folderId = selectedRecord.locationId;

        saveLastExplorerLocationFromRecord(userId, selectedRecord);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call loadProjectFile modal
        showModal("loadProjectFile");
    }

    function openWorkOrder(description) {
        // Add folderId to description object
        description.folderId = selectedRecord.locationId;

        saveLastExplorerLocationFromRecord(userId, selectedRecord);
        
        setExternalDataState({
            platform: "Work Order",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call workOrderImport modal
        showModal("workOrderImport");
    }
    
    async function deleteRecord() {
        if (!selectedRecord) return;
    
        // Show confirmation dialog using SweetAlert
        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.destructive,
            title: m.explorer_info_delete_title(),
            text: m.explorer_info_delete_text({ name: selectedRecord.name }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.explorer_info_delete_confirm(),
            cancelButtonText: m.explorer_info_cancel_button(),
        });

        if (!result.isConfirmed) return;
    
        try {
            // Determine which database reference to use
            const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
        
            // Delete the record from Firestore
            await dbRef.doc(selectedRecord.id).delete();
        
            // If it's a project, also delete the project file from storage
            if (selectedRecord.type === "project" || selectedRecord.type === "team project" || selectedRecord.type === "work order") {
                const projectFilePath = selectedRecord.rootDir === "personal" 
                    ? "users/" + userId + "/projects/" + selectedRecord.id
                    : "teams/" + teamId + "/storage/" + selectedRecord.id;
                
                if (selectedRecord.rootDir === "team") {
                    // Use Cloud Function for team projects
                    await firebase.functions().httpsCallable("v8DeleteProjectFile")(projectFilePath);
                } else {
                    // Use direct storage reference for personal projects
                    await storage.ref().child(projectFilePath).delete();
                }
            }
        
            // Update the explorer state to remove the deleted record
            explorerState.records = explorerState.records.filter(record => record.id !== selectedRecord.id);
        
            // Clear selection if the deleted record was selected
            if (explorerState.selectedRecords.includes(selectedRecord.id)) {
                explorerState.selectedRecords = explorerState.selectedRecords.filter(id => id !== selectedRecord.id);
            }
        
            // Show success message
            toast.push(m.explorer_info_delete_success_toast({ name: selectedRecord.name }), {
                classes: ["toast-success"]
            });
        
            // Clear the selected record
            selectedRecord = null;
        } catch (error) {
            console.error('Error deleting record:', error);
            toast.push(m.explorer_info_delete_failed_toast({ name: selectedRecord.name, error: error.message }), {
                classes: ["toast-danger"]
            });
        }
    }
    
    async function overwriteProject(record) {
        if (loading) return;
        
        try {
            loading = true;
            
            // Check if record is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                throw new Error(m.explorer_info_overwrite_locked_error({ name: record.name, lockedBy: record.lockedBy }));
            }
            
            // Show confirmation dialog
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.destructiveDeny,
                titleText: m.explorer_info_overwrite_title(),
                text: m.explorer_info_overwrite_text({ name: record.name }),
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: m.explorer_info_overwrite_confirm(),
                denyButtonText: m.explorer_info_cancel_button(),
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
            
            if (!result.isConfirmed) {
                throw new Error(m.explorer_info_overwrite_cancelled_error());
            }
            
            // Update the project to use the same ID as the selected record
            updateProjectId(record.id);
            
            // Determine file path based on storage location
            const projectFilePath = record.rootDir === "team"
                ? teamStorageBasePath + record.id
                : homeStorageBasePath + record.id;
                
            // Create project JSON
            const projectJson = ccprjFunc.createCcprj({
                project: JSON.parse(JSON.stringify(projectState)),
                metadata: JSON.parse(JSON.stringify(metadataState)),
                speakers: JSON.parse(JSON.stringify(speakerState)),
                issues: JSON.parse(JSON.stringify(issueState)),
                markers: JSON.parse(JSON.stringify(markerState)),
                eventGroups: JSON.parse(JSON.stringify(eventGroupState)),
                style: JSON.parse(JSON.stringify(styleState))
            });

            projectJson.rootDir = explorerState.rootDir;
            
            // Upload to storage
            const uploadProjectRef = storage.ref().child(projectFilePath);
            await uploadProjectRef.putString(JSON.stringify(projectJson));
            
            // Update project record in database
            const projectInfo = {
                id: record.id,
                name: projectState.name,
                type: projectState.type === "team" ? "team project" : "project",
                owner: userEmail,
                createdOn: record.createdOn,
                updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
                locationId: record.locationId,
                shared: record.rootDir === "team" ? true : false,
                star: record.star || false,
                description: record.description || "",
                rootDir: record.rootDir,
                assignedTo: record.assignedTo ?? null,
                state: record.state ?? null,
                locked: record.locked || false,
                lockedBy: record.lockedBy ?? null,
                lockedOn: record.lockedOn ?? null,
                dueDate: record.dueDate ?? null,
                priority: record.priority ?? null,
                totalEvents: record.totalEvents ?? null,
                eventGroups: record.eventGroups ?? null,
            };
            
            const dbRef = record.rootDir === "team" ? teamDbRef : homeDbRef;
            await dbRef.doc(projectInfo.id).set(projectInfo);
            
            // Update project state with location info
            projectState.folderId = record.locationId;
            projectState.rootDir = record.rootDir;
            
            // Reset history and show success message
            resetHistory();
            insertHistoryAction({
                name: "save project", //action name
                eventGroup: projectState.selected,
                snapshots: [
                    {
                        store: "eventGroupState",
                        value: JSON.stringify(eventGroupState),
                    }
                ],
            });
            toast.push(m.explorer_info_overwrite_success_toast({ name: projectState.name }), {
                classes: ["toast-success"],
            });
            
            // Update the record in the records list
            explorerState.records = explorerState.records.map(r => 
                r.id === record.id ? projectInfo : r
            );

            saveLastExplorerLocationFromRecord(userId, record);
            
            hideModal();
        } catch (error) {
            console.error("Error overwriting project:", error);
            toast.push(m.explorer_info_overwrite_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            loading = false;
        }
    }
    
    function starRecord() {
        console.log('Starring record:', selectedRecord.id);
        updateStarStatus(true);
    }
    
    function unstarRecord() {
        console.log('Unstarring record:', selectedRecord.id);
        updateStarStatus(false);
    }
    
    async function updateStarStatus(starValue) {
        try {
            // Determine which database reference to use based on rootDir
            const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
            
            // Update the document star status in Firestore
            await dbRef.doc(selectedRecord.id).update({
                star: starValue
            });
            
            // Update the local record in the explorer state
            explorerState.records = explorerState.records.map(record => {
                if (record.id === selectedRecord.id) {
                    return {...record, star: starValue};
                }
                return record;
            });

            toast.push(starValue ? m.explorer_info_starred_success_toast() : m.explorer_info_unstarred_success_toast(), {
                classes: ["toast-success"]
            });
            
            // Update the selected record to reflect the change
            selectedRecord = {...selectedRecord, star: starValue};
        } catch (error) {
            console.error('Error updating star status:', error);
            toast.push(error.message, {
                classes: ["toast-danger"],
            });
        }
    }

    async function updateState(){
        console.log('Updating state for record:', selectedRecord.id);
        //Use Sweet Alert to ask the user to select a new state from a select dropdown list. The dropdown list will contain the states stored in workflowDict.states
        const { value: newState } = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_state_select_title(),
            input: 'select',
            inputOptions: workflowDict.states,
            inputPlaceholder: m.explorer_info_state_select_placeholder(),
            showCancelButton: true,
            inputValidator: (value) => {
                return new Promise((resolve) => {
                    if (value !== '') {
                        resolve();
                    } else {
                        resolve(m.explorer_info_state_select_validation());
                    }
                });
            }
        });

        if (workflowDict.states[newState]) {
            console.log('New state selected:', newState);
            //Update the state of the selected record in firestore
            try {
                // Determine which database reference to use based on rootDir
                const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
                
                // Update the document state in Firestore
                await dbRef.doc(selectedRecord.id).update({
                    state: workflowDict.states[newState]
                });
                
                // Update the local record in the explorer state
                explorerState.records = explorerState.records.map(record => {
                    if (record.id === selectedRecord.id) {
                        return {...record, state: workflowDict.states[newState]};
                    }
                    return record;
                });

                toast.push(m.explorer_info_state_updated_toast({ state: workflowDict.states[newState] }), {
                    classes: ["toast-success"]
                });
            } catch (error) {
                console.error('Error updating state:', error);
                toast.push(error.message, {
                    classes: ["toast-danger"],
                });
            }
        }
    }

    async function updateDescription() {
        if (!selectedRecord) return;
        if (selectedRecord.locked && selectedRecord.lockedBy !== userEmail) return;

        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_description_title(),
            input: "textarea",
            inputValue: selectedRecord.description || "",
            inputPlaceholder: m.explorer_info_description_placeholder(),
            showCancelButton: true,
            confirmButtonText: m.explorer_info_description_save_button(),
            cancelButtonText: m.explorer_info_cancel_button(),
        });

        if (!result.isConfirmed) return;

        try {
            const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
            const currentTime = firebase.firestore.Timestamp.fromDate(new Date());
            const description = result.value || "";

            await dbRef.doc(selectedRecord.id).update({
                description,
                updatedOn: currentTime
            });

            explorerState.records = explorerState.records.map(record => {
                if (record.id === selectedRecord.id) {
                    return {
                        ...record,
                        description,
                        updatedOn: currentTime
                    };
                }
                return record;
            });

            selectedRecord = {
                ...selectedRecord,
                description,
                updatedOn: currentTime
            };

            toast.push(m.explorer_info_description_updated_toast(), {
                classes: ["toast-success"]
            });
        } catch (error) {
            console.error("Error updating description:", error);
            toast.push(m.explorer_info_description_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }

    // Implementation for lock and unlock features
    async function updateLockStatus(lockValue) {
        try {
            // Determine which database reference to use based on rootDir
            const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
            
            // Update the document lock status in Firestore
            await dbRef.doc(selectedRecord.id).update({
                locked: lockValue,
                lockedBy: lockValue ? userEmail : null,
                lockedOn: lockValue ? firebase.firestore.Timestamp.fromDate(new Date()) : null
            });
            
            // Update the local record in the explorer state
            explorerState.records = explorerState.records.map(record => {
                if (record.id === selectedRecord.id) {
                    return {
                        ...record, 
                        locked: lockValue,
                        lockedBy: lockValue ? userEmail : null,
                        lockedOn: lockValue ? firebase.firestore.Timestamp.fromDate(new Date()) : null
                    };
                }
                return record;
            });

            toast.push(lockValue ? m.explorer_info_locked_success_toast() : m.explorer_info_unlocked_success_toast(), {
                classes: ["toast-success"]
            });
            
            // Update the selected record to reflect the change
            selectedRecord = {
                ...selectedRecord, 
                locked: lockValue,
                lockedBy: lockValue ? userEmail : null,
                lockedOn: lockValue ? firebase.firestore.Timestamp.fromDate(new Date()) : null
            };
        } catch (error) {
            console.error('Error updating lock status:', error);
            toast.push(error.message, {
                classes: ["toast-danger"],
            });
        }
    }

    function lockRecord() {
        // First confirm with the user
        Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_lock_title(),
            text: m.explorer_info_lock_text({ name: selectedRecord.name }),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: m.explorer_info_lock_confirm(),
            cancelButtonText: m.explorer_info_cancel_button(),
        }).then((result) => {
            if (result.isConfirmed) {
                updateLockStatus(true);
            }
        });
    }

    function unlockRecord() {
        // First confirm with the user
        Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_unlock_title(),
            text: m.explorer_info_unlock_text({ name: selectedRecord.name }),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: m.explorer_info_unlock_confirm(),
            cancelButtonText: m.explorer_info_cancel_button(),
        }).then((result) => {
            if (result.isConfirmed) {
                updateLockStatus(false);
            }
        });
    }

    async function persistAssignmentEmailMode(mode) {
        const snap = await userProfileRef.get();
        const notifications = {
            ...(snap.exists ? snap.data().notifications || {} : {}),
            assignmentEmailMode: mode,
        };
        await userProfileRef.set({ notifications }, { merge: true });
    }

    async function invokeAssignmentEmailCallable(projectId, rootDir, assigneeEmail) {
        const scope = rootDir === "personal" ? "personal" : "team";
        const payload = {
            projectId,
            assigneeEmail,
            scope,
        };
        if (scope === "team") {
            const resolvedTeamId = authState.team?.id ?? teamId;
            if (!resolvedTeamId) {
                throw new Error("Team ID is required for team projects.");
            }
            payload.teamId = resolvedTeamId;
        }
        await firebase.functions().httpsCallable("v8SendProjectAssignmentEmail")(payload);
    }

    function formatAssignmentEmailMessage(key, params = {}, fallback = "") {
        const messageFunc = m[key];
        const message = typeof messageFunc === "function" ? messageFunc(params) : fallback;
        return Object.entries(params).reduce((text, [paramKey, value]) => {
            return text.replaceAll(`{${paramKey}}`, value ?? "");
        }, message);
    }

    function escapeHtml(value = "") {
        return String(value)
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");
    }

    async function maybeOfferAssignmentEmailNotification(assignValue, ctx) {
        if (!assignValue) return;

        const snap = await userProfileRef.get();
        const mode = snap.exists ? snap.data().notifications?.assignmentEmailMode : undefined;

        if (mode === "never_send") return;

        const sendWithToast = async () => {
            try {
                await invokeAssignmentEmailCallable(ctx.projectId, ctx.rootDir, assignValue);
                toast.push(formatAssignmentEmailMessage(
                    "explorer_assignment_email_sent_toast",
                    {},
                    "Assignment notification email sent"
                ), {
                    classes: ["toast-success"],
                });
            } catch (err) {
                const msg =
                    err?.message ||
                    err?.details ||
                    String(err);
                toast.push(formatAssignmentEmailMessage(
                    "explorer_assignment_email_failed_toast",
                    { error: msg },
                    "Could not send email: {error}"
                ), {
                    classes: ["toast-danger"],
                });
            }
        };

        if (mode === "always_send") {
            await sendWithToast();
            return;
        }

        let dontAskAgain = false;
        const promptParams = {
            assignee: assignValue,
            project: ctx.projectName,
        };
        const swalResult = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: formatAssignmentEmailMessage(
                "explorer_assignment_email_prompt_title",
                {},
                "Send email notification?"
            ),
            html: `<p class="text-start mb-3">${escapeHtml(formatAssignmentEmailMessage(
                "explorer_assignment_email_prompt_text",
                promptParams,
                "Send an email to {assignee} letting them know they were assigned to \"{project}\"?"
            ))}</p><div class="form-check text-start"><input class="form-check-input" type="checkbox" id="assignment-email-dont-ask"><label class="form-check-label" for="assignment-email-dont-ask">${escapeHtml(formatAssignmentEmailMessage(
                "explorer_assignment_email_dont_ask_again",
                {},
                "Don't ask me again"
            ))}</label></div>`,
            showCancelButton: true,
            confirmButtonText: formatAssignmentEmailMessage(
                "explorer_assignment_email_send_button",
                {},
                "Send email"
            ),
            cancelButtonText: formatAssignmentEmailMessage(
                "explorer_assignment_email_skip_button",
                {},
                "No thanks"
            ),
            didOpen: () => {
                const cb = document.getElementById("assignment-email-dont-ask");
                cb?.addEventListener("change", (e) => {
                    dontAskAgain = e.target.checked;
                });
            },
            preConfirm: () => {
                const cb = document.getElementById("assignment-email-dont-ask");
                dontAskAgain = !!cb?.checked;
            },
        });

        if (swalResult.isConfirmed) {
            if (dontAskAgain) {
                await persistAssignmentEmailMode("always_send");
            }
            await sendWithToast();
        } else if (dontAskAgain) {
            await persistAssignmentEmailMode("never_send");
        }
    }

    // Function to update the assigned user
    async function updateAssignedUser() {
        if (!selectedRecord) return;
        
        // Prepare the options for the dropdown
        let assignmentOptions = {};
        
        if (authState.team && authState.team.members && authState.team.members.length > 0) {
            // If user has a team, show all team members
            assignmentOptions = authState.team.members.reduce((options, member) => {
                options[member] = member;
                return options;
            }, {});
        } else {
            // If user doesn't have a team, only show the user's email
            assignmentOptions = { [userEmail]: userEmail };
        }
        
        // Add an option to clear the assignment
        assignmentOptions.none = m.explorer_info_assign_none_option();
        
        // Show SweetAlert with dropdown
        let { value: assignedEmail } = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_assign_title(),
            input: 'select',
            inputOptions: assignmentOptions,
            inputPlaceholder: m.explorer_info_assign_placeholder(),
            showCancelButton: true,
            inputValue: selectedRecord.assignedTo || '',
            confirmButtonText: m.explorer_info_assign_confirm(),
            cancelButtonText: m.explorer_info_cancel_button(),
        });
        
        if (assignedEmail) {
            try {
                // Determine which database reference to use based on rootDir
                const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
                
                // Set the assigned user (or null if 'none' was selected)
                const assignValue = assignedEmail === 'none' ? null : assignedEmail;
                const updatedOn = firebase.firestore.Timestamp.fromDate(new Date());
                
                // Update the document in Firestore
                await dbRef.doc(selectedRecord.id).update({
                    assignedTo: assignValue,
                    updatedOn
                });

                const assignmentEmailCtx = {
                    projectId: selectedRecord.id,
                    rootDir: selectedRecord.rootDir,
                    projectName: selectedRecord.name || "",
                };

                if (explorerState.rootDir === "tasks" && assignValue !== userEmail) {
                    explorerState.records = explorerState.records.filter(record =>
                        !(record.id === selectedRecord.id && record.rootDir === selectedRecord.rootDir)
                    );
                    explorerState.selectedRecords = explorerState.selectedRecords.filter(recordId => recordId !== selectedRecord.id);
                    selectedRecord = null;

                    toast.push(m.explorer_info_assignment_updated_toast(), {
                        classes: ["toast-success"]
                    });

                    await maybeOfferAssignmentEmailNotification(assignValue, assignmentEmailCtx);

                    return;
                }
                
                // Update the local record in the explorer state
                explorerState.records = explorerState.records.map(record => {
                    if (record.id === selectedRecord.id && record.rootDir === selectedRecord.rootDir) {
                        return {
                            ...record,
                            assignedTo: assignValue,
                            updatedOn
                        };
                    }
                    return record;
                });

                toast.push(m.explorer_info_assignment_updated_toast(), {
                    classes: ["toast-success"]
                });
                
                // Update the selected record
                selectedRecord = {
                    ...selectedRecord,
                    assignedTo: assignValue,
                    updatedOn
                };

                await maybeOfferAssignmentEmailNotification(assignValue, assignmentEmailCtx);
            } catch (error) {
                console.error('Error updating assignment:', error);
                toast.push(error.message, {
                    classes: ["toast-danger"],
                });
            }
        }
    }

    function formatDueDateInputValue(dueDate) {
        if (!dueDate || typeof dueDate.toDate !== "function") return "";
        const date = dueDate.toDate();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    async function updateDueDate() {
        if (!selectedRecord) return;
        if (selectedRecord.locked && selectedRecord.lockedBy !== userEmail) return;

        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_due_date_title(),
            input: "date",
            inputValue: formatDueDateInputValue(selectedRecord.dueDate),
            showCancelButton: true,
            showDenyButton: !!selectedRecord.dueDate,
            confirmButtonText: m.explorer_info_due_date_confirm(),
            denyButtonText: m.explorer_info_due_date_clear_option(),
            cancelButtonText: m.explorer_info_cancel_button(),
        });

        if (!result.isConfirmed && !result.isDenied) return;

        try {
            const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
            const currentTime = firebase.firestore.Timestamp.fromDate(new Date());
            let dueDate = null;

            if (result.isConfirmed && result.value) {
                const [year, month, day] = result.value.split("-").map(Number);
                dueDate = firebase.firestore.Timestamp.fromDate(new Date(year, month - 1, day));
            }

            await dbRef.doc(selectedRecord.id).update({
                dueDate,
                updatedOn: currentTime
            });

            explorerState.records = explorerState.records.map(record => {
                if (record.id === selectedRecord.id && record.rootDir === selectedRecord.rootDir) {
                    return {
                        ...record,
                        dueDate,
                        updatedOn: currentTime
                    };
                }
                return record;
            });

            selectedRecord = {
                ...selectedRecord,
                dueDate,
                updatedOn: currentTime
            };

            toast.push(m.explorer_info_due_date_updated_toast(), {
                classes: ["toast-success"]
            });
        } catch (error) {
            console.error("Error updating due date:", error);
            toast.push(m.explorer_info_due_date_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }

    async function updatePriority() {
        if (!selectedRecord) return;
        if (selectedRecord.locked && selectedRecord.lockedBy !== userEmail) return;

        const priorityOptions = { none: m.explorer_info_priority_none_option() };
        for (let i = 1; i <= 10; i++) {
            priorityOptions[String(i)] = String(i);
        }

        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_priority_title(),
            input: "select",
            inputOptions: priorityOptions,
            inputPlaceholder: m.explorer_info_priority_placeholder(),
            inputValue: selectedRecord.priority != null ? String(selectedRecord.priority) : "none",
            showCancelButton: true,
            confirmButtonText: m.explorer_info_priority_confirm(),
            cancelButtonText: m.explorer_info_cancel_button(),
        });

        if (!result.isConfirmed || result.value === undefined || result.value === "") return;

        try {
            const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
            const currentTime = firebase.firestore.Timestamp.fromDate(new Date());
            const priority = result.value === "none" ? null : Number(result.value);

            await dbRef.doc(selectedRecord.id).update({
                priority,
                updatedOn: currentTime
            });

            explorerState.records = explorerState.records.map(record => {
                if (record.id === selectedRecord.id && record.rootDir === selectedRecord.rootDir) {
                    return {
                        ...record,
                        priority,
                        updatedOn: currentTime
                    };
                }
                return record;
            });

            selectedRecord = {
                ...selectedRecord,
                priority,
                updatedOn: currentTime
            };

            toast.push(m.explorer_info_priority_updated_toast(), {
                classes: ["toast-success"]
            });
        } catch (error) {
            console.error("Error updating priority:", error);
            toast.push(m.explorer_info_priority_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }

    // Function to rename the selected record
    async function renameRecord() {
        if (!selectedRecord) return;
        
        // Show SweetAlert with input field
        const { value: newName } = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.explorer_info_rename_title(),
            input: 'text',
            inputLabel: m.explorer_info_rename_label(),
            inputValue: selectedRecord.name,
            showCancelButton: true,
            inputValidator: (value) => {
                if (!value) {
                    return m.explorer_info_rename_validation();
                }
            },
        });
        
        if (newName && newName !== selectedRecord.name) {
            try {
                // Determine which database reference to use based on rootDir
                const dbRef = selectedRecord.rootDir === "personal" ? homeDbRef : teamDbRef;
                
                // Update the document name in Firestore
                await dbRef.doc(selectedRecord.id).update({
                    name: newName,
                    updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
                });
                
                // Update the local record in the explorer state
                explorerState.records = explorerState.records.map(record => {
                    if (record.id === selectedRecord.id) {
                        return {
                            ...record,
                            name: newName,
                            updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
                        };
                    }
                    return record;
                });

                toast.push(m.explorer_info_rename_success_toast({ name: newName }), {
                    classes: ["toast-success"]
                });
                
                // Update the selected record
                selectedRecord = {
                    ...selectedRecord,
                    name: newName,
                    updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
                };
            } catch (error) {
                console.error('Error renaming record:', error);
                toast.push(error.message, {
                    classes: ["toast-danger"],
                });
            }
        }
    }
    
    // Function to load available groups for the current team
    async function loadAvailableGroups() {
        if (!teamId || !isAdminOrManager) return;
        
        loadingGroups = true;
        try {
            const groupsSnapshot = await db.collection('teams').doc(teamId).collection('groups').get();
            availableGroups = [];
            groupsSnapshot.forEach(doc => {
                availableGroups.push(doc.data());
            });
        } catch (error) {
            console.error("Error loading groups:", error);
            toast.push(m.explorer_info_load_groups_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            loadingGroups = false;
        }
    }
    
    // Function to manage folder permissions
    async function manageFolderPermissions() {
        if (!selectedRecord || selectedRecord.type !== "folder" || selectedRecord.rootDir !== "team" || !isAdminOrManager) return;
        
        try {
            // Ensure we have the latest groups
            await loadAvailableGroups();
            
            // Prepare the list of checkboxes for each group
            const groupInputs = availableGroups.map(group => {
                const isAssigned = selectedRecord.groups && selectedRecord.groups.includes(group.id);
                return `
                    <div class="form-check mb-2">
                        <input class="form-check-input" type="checkbox" id="group-${group.id}" ${isAssigned ? 'checked' : ''}>
                        <label class="form-check-label" for="group-${group.id}">
                            ${group.name}
                        </label>
                    </div>
                `;
            }).join('');
            
            // Show SweetAlert with the group checkboxes
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.explorer_info_permissions_title(),
                html: `
                    <div class="text-start">
                        <p>${m.explorer_info_permissions_description()}</p>
                        <p class="text-secondary small">${m.explorer_info_permissions_note()}</p>
                        <div class="mt-3">
                            ${groupInputs}
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: m.explorer_info_permissions_save_button(),
                preConfirm: () => {
                    // Get all selected group IDs
                    return availableGroups
                        .filter(group => document.getElementById(`group-${group.id}`).checked)
                        .map(group => group.id);
                }
            });
            
            if (result.isConfirmed) {
                const selectedGroups = result.value;
                
                // Update the folder's permissions in Firestore
                console.log("Updating folder permissions to groups:", selectedGroups);
                console.log(selectedRecord.id);
                await teamDbRef.doc(selectedRecord.id).update({
                    groups: selectedGroups,
                    updatedOn: firebase.firestore.FieldValue.serverTimestamp()
                });
                
                // Update the local record
                selectedRecord.groups = selectedGroups;
                explorerState.records = explorerState.records.map(r => 
                    r.id === selectedRecord.id ? {...r, groups: selectedGroups} : r
                );
                
                toast.push(m.explorer_info_permissions_updated_toast(), {
                    classes: ["toast-success"],
                });
            }
        } catch (error) {
            console.error("Error managing folder permissions:", error);
            toast.push(m.explorer_info_permissions_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }
</script>
<div class="col-3 bg-light rounded p-3">
    {#if batchExporting}
    <!-- Batch Export progress -->
    <h4 class="my-1"><i class="bi bi-cloud-download"></i> {m.explorer_info_batch_exporting_title()}</h4>
    <p class="text-muted small mt-2 mb-1">{batchStatusMsg}</p>
    <div class="progress" role="progressbar" aria-valuenow={batchProgress} aria-valuemin="0" aria-valuemax="100" style="height: 25px;">
        <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: {batchProgress}%;">
            {batchProgress}%
        </div>
    </div>
    {:else if multiSelect}
    <!-- Multi-select summary + Batch Export -->
    <h4 class="my-1"><i class="bi bi-collection"></i> {m.explorer_info_batch_selected_count({ count: selectedProjectRecords.length })}</h4>
    <p class="text-muted small mt-2">{m.explorer_info_batch_selected_hint()}</p>
    <div id="batch-selected-list" class="border rounded my-2" style="max-height: 260px; overflow-y: auto;">
        <ul class="list-group list-group-flush">
            {#each selectedProjectRecords as record (record.id)}
                <li class="list-group-item bg-transparent py-1 small text-truncate">
                    <i class="bi {iconMap[record.type]}"></i> {record.name}
                </li>
            {/each}
        </ul>
    </div>
    {#if environment.online}
    <button type="button" class="w-100 btn btn-lg btn-primary" onclick={batchExportSelected}>
        <i class="bi bi-cloud-download"></i> {m.explorer_info_batch_export_button()}
    </button>
    {/if}
    {:else if selectedRecord}
    <p class="text-muted m-0 small d-flex align-items-center">
        {selectedRecord.id}
        <button aria-label={m.explorer_info_copy_id_aria()} type="button" class="btn btn-sm btn-link p-0 ms-1" onclick={copyIdToClipboard} title={m.explorer_info_copy_id_title()}>
            <i class="bi bi-clipboard"></i>
        </button>
    </p>
    <h4 class="my-1"><i class="bi {iconMap[selectedRecord.type]}"></i> {selectedRecord.name}</h4>
    <div id="workspace-table-view" class="table-responsive border rounded border-0 my-2">
        <table class="table table-light border-secondary">
            <thead class="bg-light">
                <tr>
                    <th>{m.explorer_info_property_header()}</th>
                    <th>{m.explorer_info_value_header()}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{m.explorer_info_property_star()}</td>
                    <td>{#if selectedRecord.star}<i class="bi bi-star-fill"></i>{:else}-{/if}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_state()}</td>
                    <td>{selectedRecord.state || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_description()}</td>
                    <td class="text-break">{selectedRecord.description || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_lock_status()}</td>
                    <td>
                        {#if selectedRecord.locked}
                            <span class="text-danger"><i class="bi bi-lock-fill"></i> {m.explorer_info_locked_label()}</span>
                        {:else}
                            <i class="bi bi-unlock"></i>
                        {/if}
                    </td>
                </tr>
                {#if selectedRecord.locked}
                <tr>
                    <td>{m.explorer_info_property_locked_by()}</td>
                    <td>{selectedRecord.lockedBy || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_locked_on()}</td>
                    <td>{selectedRecord.lockedOn ? selectedRecord.lockedOn.toDate().toLocaleDateString() : "-"}</td>
                </tr>
                {/if}
                <tr>
                    <td>{m.explorer_info_property_owner()}</td>
                    <td>{selectedRecord.owner || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_assigned_to()}</td>
                    <td>{selectedRecord.assignedTo || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_due_date()}</td>
                    <td>{selectedRecord.dueDate ? selectedRecord.dueDate.toDate().toLocaleDateString() : "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_priority()}</td>
                    <td>{selectedRecord.priority != null ? selectedRecord.priority : "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_event_groups()}</td>
                    <td>{selectedRecord.eventGroups || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_total_events()}</td>
                    <td>{selectedRecord.totalEvents || "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_last_modified()}</td>
                    <td>{selectedRecord.updatedOn ? selectedRecord.updatedOn.toDate().toLocaleDateString() : "-"}</td>
                </tr>
                <tr>
                    <td>{m.explorer_info_property_created_on()}</td>
                    <td>{selectedRecord.createdOn ? selectedRecord.createdOn.toDate().toLocaleDateString() : "-"}</td>
                </tr>
                <!-- Storage Root Dir -->
                <tr>
                    <td>{m.explorer_info_property_storage()}</td>
                    <td class="text-capitalize">{selectedRecord.rootDir || "-"}</td>
                </tr>
                <!-- Location ID -->
                <tr>
                    <td>{m.explorer_info_property_location_id()}</td>
                    <td>{selectedRecord.locationId || "/"}</td>
                </tr>
            </tbody>
        </table>
    </div>
      <!-- Action Buttons -->
      <!-- Open Folder -->
      {#if selectedRecord.type === "folder"}
      <button type="button" class="w-100 btn btn-lg btn-outline-primary" onclick={openRecord} disabled={openLoading}>
        {#if openLoading}
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span class="ms-1">{m.explorer_info_opening_label()}</span>
        {:else}
            <i class="bi bi-folder2-open"></i> {m.explorer_info_open_folder_button()}
        {/if}
      </button>
      <hr/>
      {:else if explorerState.context === "load" && selectedRecord.type !== "folder"}
      <!-- Open Record (not folder) -->
      <button type="button" class="w-100 btn btn-lg btn-outline-primary" onclick={openRecord} disabled={openLoading}>
        {#if openLoading}
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span class="ms-1">{m.explorer_info_opening_label()}</span>
        {:else}
            <i class="bi bi-folder-symlink"></i> {m.explorer_info_open_project_button()}
        {/if}
      </button>
      <hr/>
      {:else if explorerState.context === "save" && selectedRecord.type !== "folder"}
      <button type="button" class="w-100 btn btn-lg btn-outline-secondary" onclick={() => overwriteProject(selectedRecord)} disabled={loading}>
        {#if loading}
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span class="ms-1">{m.explorer_info_saving_label()}</span>
        {:else}
            <i class="bi bi-arrow-repeat"></i> {m.explorer_info_save_overwrite_button()}
        {/if}
      </button>
      <hr/>
      {/if}

    <div class="btn-toolbar" role="toolbar" aria-label={m.explorer_info_toolbar_label()}>
        <div class="btn-group btn-group-sm me-2" role="group" aria-label={m.explorer_info_record_actions_label()}>
            <!-- Rename Button -->
            <button type="button" title={m.explorer_info_rename_button()} class="btn btn-outline-warning" onclick={renameRecord}
                disabled={selectedRecord.locked && selectedRecord.lockedBy !== userEmail}>
                <i class="bi bi-pencil-square"></i>
            </button>

            <button aria-label={m.explorer_info_description_aria()} type="button" class="btn btn-outline-warning" onclick={updateDescription}
                disabled={selectedRecord.locked && selectedRecord.lockedBy !== userEmail}>
                <i class="bi bi-journal-text"></i>
            </button>

            <!-- Update State -->
            {#if selectedRecord.type !== "folder"}
            <button type="button" title={m.explorer_info_state_button()} class="btn btn-outline-warning" onclick={updateState}>
                <i class="bi bi-diagram-2"></i>
            </button>
            {/if}

            <!-- Assign To User -->
            {#if selectedRecord.type !== "folder"}
            <button aria-label={m.explorer_info_assign_user_aria()} type="button" class="btn btn-outline-warning" onclick={updateAssignedUser}>
                <i class="bi bi-person"></i>
            </button>
            <button aria-label={m.explorer_info_due_date_aria()} type="button" class="btn btn-outline-warning" onclick={updateDueDate}
                disabled={selectedRecord.locked && selectedRecord.lockedBy !== userEmail}>
                <i class="bi bi-calendar-event"></i>
            </button>
            <button aria-label={m.explorer_info_priority_aria()} type="button" class="btn btn-outline-warning" onclick={updatePriority}
                disabled={selectedRecord.locked && selectedRecord.lockedBy !== userEmail}>
                <i class="bi bi-flag"></i>
            </button>
            {/if}

            <!-- STAR RECORD -->
            {#if selectedRecord.star}
            <button aria-label={m.explorer_info_unstar_aria()} type="button" class="btn btn-outline-warning" onclick={() => unstarRecord()} class:d-none={!selectedRecord.star}>
                <i class="bi bi-star-fill"></i>
            </button>
            {:else}
            <button aria-label={m.explorer_info_star_aria()} type="button" class="btn btn-outline-warning" onclick={() => starRecord()} class:d-none={selectedRecord.star}>
                <i class="bi bi-star"></i>
            </button>
            {/if}

            <!-- LOCK RECORD -->
            {#if selectedRecord.locked && selectedRecord.type !== "folder"}        
                <button aria-label={m.explorer_info_unlock_aria()} type="button" class="btn btn-outline-warning" onclick={unlockRecord} 
                    disabled={selectedRecord.lockedBy && selectedRecord.lockedBy !== userEmail}>
                    <i class="bi bi-unlock"></i>
                </button>
            {:else if selectedRecord.type !== "folder"}
                <button aria-label={m.explorer_info_lock_aria()} type="button" class="btn btn-outline-warning" onclick={lockRecord}>
                    <i class="bi bi-lock"></i>
                </button>
            {/if}

            <!-- BATCH EXPORT - Online, non-folder records only -->
            {#if selectedRecord.type !== "folder" && environment.online}
                <button aria-label={m.explorer_info_batch_export_button()} title={m.explorer_info_batch_export_button()} type="button" class="btn btn-outline-warning" onclick={batchExportSelected}>
                    <i class="bi bi-cloud-download"></i>
                </button>
            {/if}
            
            <!-- PERMISSIONS BUTTON - Only for team folders when user is admin/manager -->
            {#if selectedRecord.type === "folder" && selectedRecord.rootDir === "team" && isAdminOrManager}
                <button aria-label={m.explorer_info_permissions_aria()} type="button" class="btn btn-outline-warning" onclick={manageFolderPermissions} disabled={loadingGroups}>
                    {#if loadingGroups}
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {:else}
                        <i class="bi bi-people"></i>
                    {/if}
                </button>
            {/if}

            
        </div>
        <div class="btn-group btn-group-sm" role="group" aria-label={m.explorer_info_record_actions_label()}>
            <button aria-label={m.explorer_info_delete_aria()} type="button" class="btn btn-outline-danger" onclick={deleteRecord}
                disabled={selectedRecord.locked && selectedRecord.lockedBy !== userEmail}>
                <i class="bi bi-trash"></i>
            </button>
        </div>
    </div>
    {:else}
    <h4>{m.explorer_info_no_record_selected()}</h4>
    {/if}
    
</div>